export const RESULT_ITEM_TYPES = [
  'key_strength',
  'professional_growth_challenge_opportunity',
  'suggested_career_path',
  'skill_worth_developing',
];

export const SKILL_WORTH_DEVELOPING = 'skill_worth_developing';

export const RESULT_CONTENT_TABS = [
  { id: 'summaries', labelKey: 'resultContent.tabs.summaries' },
  { id: 'key_strength', labelKey: 'resultContent.tabs.keyStrengths' },
  { id: 'professional_growth_challenge_opportunity', labelKey: 'resultContent.tabs.growth' },
  { id: 'suggested_career_path', labelKey: 'resultContent.tabs.careerPaths' },
  { id: 'skill_worth_developing', labelKey: 'resultContent.tabs.skills' },
  { id: 'tags', labelKey: 'resultContent.tabs.tags' },
];

export function typeLabelKey(type) {
  const aliases = {
    growth_opportunity: 'professional_growth_challenge_opportunity',
    career_path: 'suggested_career_path',
    skill: 'skill_worth_developing',
    skills: 'skill_worth_developing',
  };
  const canonical = aliases[type] || type;
  return `resultItems.types.${canonical}`;
}

export function requiresSkillAdvice(type) {
  return type === SKILL_WORTH_DEVELOPING || type === 'skill' || type === 'skills';
}

export function isSelectableTag(tag) {
  return Boolean(tag) && tag.is_active !== false && !tag.is_archived;
}

export function sanitizeText(value) {
  return String(value || '').replace(/<[^>]*>/g, '').trim();
}

export function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export function localizedCopy(entity, locale, field = 'title') {
  if (!entity) {
    return '';
  }
  if (locale === 'ar') {
    return entity[`${field}_ar`] || entity[field] || entity[`${field}_en`] || '';
  }
  return entity[`${field}_en`] || entity[field] || entity[`${field}_ar`] || '';
}

export function uniqueIds(values = []) {
  const seen = new Set();
  return values
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .filter((value) => {
      if (seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
}

export function reorderItems(items, activeId, overId) {
  const next = [...(items || [])];
  const oldIndex = next.findIndex((item) => String(item.id) === String(activeId));
  const newIndex = next.findIndex((item) => String(item.id) === String(overId));
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
    return items;
  }
  const [moved] = next.splice(oldIndex, 1);
  next.splice(newIndex, 0, moved);
  return next;
}

export function buildResultItemPayload(form) {
  const type = form.type;
  const payload = {
    type,
    title_ar: sanitizeText(form.title_ar),
    title_en: sanitizeText(form.title_en),
    description_ar: sanitizeText(form.description_ar),
    description_en: sanitizeText(form.description_en),
    tag_id: Number(form.tag_id),
    is_active: Boolean(form.is_active),
  };
  if (requiresSkillAdvice(type)) {
    payload.advice_ar = sanitizeText(form.advice_ar);
    payload.advice_en = sanitizeText(form.advice_en);
  }
  return payload;
}

export function validateResultItemForm(form, t) {
  const errors = {};
  if (!sanitizeText(form.title_ar)) errors.title_ar = [t('errors.validation')];
  if (!sanitizeText(form.title_en)) errors.title_en = [t('errors.validation')];
  if (!sanitizeText(form.description_ar)) errors.description_ar = [t('errors.validation')];
  if (!sanitizeText(form.description_en)) errors.description_en = [t('errors.validation')];
  if (!form.tag_id) errors.tag_id = [t('resultContent.tagRequired')];
  if (requiresSkillAdvice(form.type)) {
    if (!sanitizeText(form.advice_ar) || !sanitizeText(form.advice_en)) {
      errors.advice_ar = [t('resultContent.skillAdviceRequired')];
      errors.advice_en = [t('resultContent.skillAdviceRequired')];
    }
  }
  return errors;
}

export function validateSummaryForm(form, t) {
  const errors = {};
  if (!sanitizeText(form.title_ar)) errors.title_ar = [t('errors.validation')];
  if (!sanitizeText(form.title_en)) errors.title_en = [t('errors.validation')];
  if (!sanitizeText(form.description_ar)) errors.description_ar = [t('errors.validation')];
  if (!sanitizeText(form.description_en)) errors.description_en = [t('errors.validation')];
  const tagIds = uniqueIds(form.tag_ids);
  if (tagIds.length === 0) {
    errors.tag_ids = [t('resultContent.tagRequired')];
  }
  return errors;
}

export function buildSummaryPayload(form) {
  return {
    title_ar: sanitizeText(form.title_ar),
    title_en: sanitizeText(form.title_en),
    description_ar: sanitizeText(form.description_ar),
    description_en: sanitizeText(form.description_en),
    tag_ids: uniqueIds(form.tag_ids),
    is_active: Boolean(form.is_active),
  };
}

export function buildTagPayload(form) {
  const payload = {
    slug: slugify(form.slug) || slugify(form.name_en),
    name_ar: sanitizeText(form.name_ar),
    name_en: sanitizeText(form.name_en),
    description_ar: sanitizeText(form.description_ar) || null,
    description_en: sanitizeText(form.description_en) || null,
    is_active: Boolean(form.is_active),
  };
  const color = String(form.color || '').trim().toUpperCase();
  if (color) {
    payload.color = color;
  } else {
    payload.color = null;
  }
  return payload;
}

export function matchesTagSearch(tag, search) {
  const term = String(search || '').trim().toLowerCase();
  if (!term) {
    return true;
  }
  const haystack = [
    tag.slug,
    tag.name_ar,
    tag.name_en,
    tag.name,
    tag.description_ar,
    tag.description_en,
    tag.description,
  ].join(' ').toLowerCase();
  return haystack.includes(term);
}

export function rtlSortableKeyboardCoordinates(sortableKeyboardCoordinates, dir) {
  return (event, args) => {
    if (dir === 'rtl' && (event.code === 'ArrowLeft' || event.code === 'ArrowRight')) {
      const swapped = event.code === 'ArrowRight' ? 'ArrowLeft' : 'ArrowRight';
      return sortableKeyboardCoordinates({ ...event, code: swapped }, args);
    }
    return sortableKeyboardCoordinates(event, args);
  };
}
